<template>
  <div
    :style="{ backgroundColor: currentTheme.bg }"
    class="min-h-screen transition-colors duration-700 font-sans"
  >
    <div
      class="w-full min-h-[90vh] flex flex-col items-center justify-center gap-4 sm:gap-6 py-4 sm:py-6"
      :class="currentTheme.text"
    >
      <div
        class="flex flex-wrap justify-center gap-2 sm:gap-3 px-2"
        :class="
          running
            ? 'opacity-20 pointer-events-none transition-opacity duration-500'
            : ''
        "
      >
        <button
          v-for="presetKey in deviceTabs"
          :key="presetKey"
          @click="device = presetKey"
          class="px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold transition"
          :class="
            device === presetKey
              ? currentTheme.tabActive + ' shadow-md'
              : 'bg-white/20 hover:bg-white/30 backdrop-blur-md border border-white/10'
          "
          :style="
            device !== presetKey
              ? {
                  color: currentTheme.isDark
                    ? 'rgba(255,255,255,0.7)'
                    : 'rgba(0,0,0,0.6)',
                }
              : {}
          "
        >
          {{ presets[presetKey].label }}
        </button>
      </div>

      <div
        ref="cardRef"
        class="relative transition-all duration-500 mx-4 shadow-2xl overflow-hidden flex flex-col"
        :style="{
          width: `min(100%, ${currentPreset.maxW}px)`,
          aspectRatio: currentPreset.ar,
          background: currentTheme.cardBg,
          borderColor: currentTheme.border,
          borderRadius: currentPreset.radius + 'px',
          borderWidth: '1px',
        }"
      >
        <login-view
          v-if="currentView === 'login'"
          :theme="currentTheme"
          :t="t"
          :device="device"
          @login="handleLoginSuccess"
          @back="currentView = 'timer'"
        ></login-view>

        <payment-view
          v-else-if="currentView === 'payment'"
          :theme="currentTheme"
          :t="t"
          :device="device"
          @success="handlePaymentSuccess"
          @back="currentView = 'timer'"
        ></payment-view>

        <membership-view
          v-else-if="currentView === 'membership'"
          :theme="currentTheme"
          :t="t"
          :device="device"
          :is-pro="isPro"
          @upgrade="currentView = 'payment'"
          @back="currentView = 'timer'"
        ></membership-view>

        <vip-success-view
          v-else-if="currentView === 'vip_success'"
          :theme="currentTheme"
          :t="t"
          @back="currentView = 'timer'"
        ></vip-success-view>

        <sync-view
          v-else-if="currentView === 'sync'"
          :theme="currentTheme"
          :t="t"
          :is-pro="isPro"
          @back="currentView = 'timer'"
        ></sync-view>

        <stats-view
          v-else-if="currentView === 'stats'"
          :theme="currentTheme"
          :t="t"
          :history="parsedHistory"
          :summary="statsSummary"
          :month-heatmap="monthHeatmap"
          :is-same-day="isSameDay"
          @back="currentView = 'timer'"
        ></stats-view>

        <div v-else class="w-full h-full flex flex-col relative">
          <div
            class="shrink-0 h-16 flex items-center justify-between px-6 z-10 relative transition-opacity duration-500"
            :class="running ? 'opacity-30 hover:opacity-100' : ''"
          >
            <div class="flex items-center gap-2">
              <button
                @click="handleProfileClick"
                class="h-9 w-9 grid place-items-center rounded-full transition active:scale-[0.97]"
                :class="[
                  currentTheme.toggle,
                  panels.profile ? 'bg-black/5 dark:bg-white/10' : '',
                  running ? 'pointer-events-none' : '',
                ]"
                :title="t.profile"
              >
                <icon-base name="User" class="w-5 h-5"></icon-base>
              </button>
            </div>
            <div class="flex items-center gap-2">
              <button
                v-if="running"
                @click="bgmOn = !bgmOn"
                class="h-9 w-9 grid place-items-center rounded-full transition active:scale-[0.97]"
                :class="currentTheme.toggle"
              >
                <icon-base name="Sound" class="w-5 h-5"></icon-base>
              </button>
              <button
                v-else
                @click="togglePanel('settings')"
                class="h-9 w-9 grid place-items-center rounded-full transition active:scale-[0.97]"
                :class="[
                  currentTheme.toggle,
                  panels.settings ? 'bg-black/5 dark:bg-white/10' : '',
                ]"
                title="设置"
              >
                <icon-base name="Brain" class="w-5 h-5"></icon-base>
              </button>
            </div>
          </div>

          <div
            class="absolute inset-x-0 top-6 flex justify-center pointer-events-none transition-opacity duration-500"
            :class="running ? 'opacity-20' : 'opacity-50'"
          >
            <div
              class="text-[15px] font-semibold select-none"
              :class="currentTheme.text"
            >
              {{ t.app_name }}
            </div>
          </div>

          <div
            class="grow flex flex-col items-center justify-center text-center px-4 relative z-0 min-h-0 overflow-hidden"
          >
            <div class="flex flex-col items-center justify-center">
              <button
                @click="!running && togglePanel('time')"
                class="select-none font-black leading-none tracking-tight transition-all duration-500"
                :class="!running ? 'hover:opacity-80' : ''"
                :style="timerStyle"
              >
                {{ fmt(seconds) }}
              </button>
              <div class="h-10 mt-2 flex items-center justify-center">
                <button
                  v-if="!running"
                  @click="togglePanel('tag')"
                  class="text-[15px] font-medium tracking-wide select-none transition hover:opacity-80 px-4 py-1.5 rounded-full border border-transparent hover:border-current"
                  :class="currentTheme.sub"
                >
                  {{ tag }}
                </button>
                <div
                  v-else-if="randomEnabled"
                  class="text-xs font-medium opacity-50 flex items-center gap-1"
                  :class="
                    currentTheme.isDark
                      ? 'text-emerald-400'
                      : 'text-emerald-600'
                  "
                >
                  <icon-base name="Zap" class="w-3 h-3"></icon-base>
                  {{ t.neuro_switch }}
                </div>
              </div>
            </div>
          </div>

          <div
            class="shrink-0 h-24 sm:h-32 flex items-center justify-center gap-4 sm:gap-6 pb-4 z-10 relative transition-opacity duration-500"
            :class="running ? 'opacity-30 hover:opacity-100' : ''"
          >
            <template v-if="running">
              <button
                @click="pause"
                :class="btnPad"
                class="rounded-full border shadow-sm active:scale-[0.98] transition-opacity bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 text-current"
                title="暂停"
              >
                <icon-base name="Pause" class="h-5 w-5"></icon-base>
              </button>
              <button
                @click="endNow"
                :class="btnPad"
                class="rounded-full border shadow-sm active:scale-[0.98] transition-opacity bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 text-current"
                title="结束"
              >
                <icon-base name="Stop" class="h-5 w-5"></icon-base>
              </button>
            </template>
            <button
              v-else
              @click="start()"
              :disabled="mode === 'down' && seconds === 0"
              class="rounded-full font-semibold transition-all active:scale-[0.98] hover:scale-105"
              :class="[
                device === 'WATCH'
                  ? 'px-8 py-3 text-base'
                  : 'px-12 py-4 text-lg',
                currentTheme.accent,
              ]"
              :style="
                mode === 'down' && seconds === 0
                  ? { opacity: 0.5, cursor: 'not-allowed' }
                  : {}
              "
            >
              {{ t.start_btn }}
            </button>
          </div>

          <div
            v-if="panels.profile && !running"
            :class="pickerClass"
            class="animate-[slideUp_0.3s_ease-out]"
          >
            <div
              class="py-4 text-center font-bold text-lg tracking-tight shrink-0 flex items-center justify-center gap-2"
            >
              <span>{{ t.profile }}</span>
            </div>

            <div class="flex-1 px-6 pb-6 overflow-y-auto min-h-0">
              <div class="flex items-center gap-4 mb-6">
                <div
                  class="w-16 h-16 rounded-full bg-gradient-to-tr from-amber-400 to-orange-500 flex items-center justify-center text-white text-2xl shadow-md border-4 border-white dark:border-white/10"
                >
                  <icon-base name="User" class="w-8 h-8"></icon-base>
                </div>
                <div>
                  <div
                    class="text-xl font-bold"
                    :class="
                      currentTheme.isDark ? 'text-white' : 'text-slate-900'
                    "
                  >
                    {{ isLoggedIn ? t.user_title : t.user_guest }}
                  </div>
                  <div
                    class="text-xs opacity-50 uppercase tracking-wide"
                    :class="
                      currentTheme.isDark ? 'text-white' : 'text-slate-500'
                    "
                  >
                    {{
                      isPro
                        ? t.user_vip
                        : isLoggedIn
                        ? t.user_level
                        : t.user_login_tip
                    }}
                  </div>
                </div>
              </div>

              <button
                @click="currentView = 'stats'"
                class="w-full grid grid-cols-3 gap-2 mb-6 p-4 rounded-2xl transition-transform active:scale-95"
                :class="
                  currentTheme.isDark
                    ? 'bg-white/5 hover:bg-white/10'
                    : 'bg-black/5 hover:bg-black/10'
                "
              >
                <div class="text-center">
                  <div class="flex justify-center mb-1 text-orange-500">
                    <icon-base name="Activity" class="w-5 h-5"></icon-base>
                  </div>
                  <div
                    class="text-lg font-bold"
                    :class="
                      currentTheme.isDark ? 'text-white' : 'text-slate-900'
                    "
                  >
                    {{ statsSummary.totalHours }}h
                  </div>
                  <div class="text-[10px] opacity-50">
                    {{ t.stats_duration }}
                  </div>
                </div>
                <div
                  class="text-center border-l border-r border-black/5 dark:border-white/5"
                >
                  <div class="flex justify-center mb-1 text-emerald-500">
                    <icon-base name="Calendar" class="w-5 h-5"></icon-base>
                  </div>
                  <div
                    class="text-lg font-bold"
                    :class="
                      currentTheme.isDark ? 'text-white' : 'text-slate-900'
                    "
                  >
                    {{ statsSummary.streak }}
                  </div>
                  <div class="text-[10px] opacity-50">
                    {{ t.stats_streak }}
                  </div>
                </div>
                <div class="text-center">
                  <div class="flex justify-center mb-1 text-blue-500">
                    <icon-base name="Award" class="w-5 h-5"></icon-base>
                  </div>
                  <div
                    class="text-lg font-bold"
                    :class="
                      currentTheme.isDark ? 'text-white' : 'text-slate-900'
                    "
                  >
                    {{ statsSummary.totalTasks }}
                  </div>
                  <div class="text-[10px] opacity-50">
                    {{ t.stats_tasks }}
                  </div>
                </div>
              </button>

              <button
                @click="currentView = 'membership'"
                class="w-full h-16 relative overflow-hidden rounded-xl mb-6 shadow-md group"
              >
                <div
                  class="absolute inset-0 z-0"
                  :class="
                    isPro
                      ? 'bg-gradient-to-r from-amber-600 to-amber-800'
                      : 'bg-gradient-to-r from-slate-900 to-slate-800'
                  "
                ></div>
                <div
                  class="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-20 z-0"
                ></div>
                <div
                  class="absolute -right-4 -top-10 w-24 h-24 rounded-full blur-2xl"
                  :class="isPro ? 'bg-white/30' : 'bg-amber-500/30'"
                ></div>
                <div
                  class="relative z-10 flex items-center justify-between px-5 h-full"
                >
                  <div class="flex flex-col items-start">
                    <div
                      class="font-bold text-sm flex items-center gap-1.5"
                      :class="isPro ? 'text-white' : 'text-amber-400'"
                    >
                      <icon-base name="Crown" class="w-4 h-4"></icon-base>
                      <span class="tracking-wide">
                        {{ t.banner_title }} {{ isPro ? t.banner_active : "" }}
                      </span>
                    </div>
                    <div
                      class="text-[10px] mt-0.5"
                      :class="isPro ? 'text-white/70' : 'text-slate-400'"
                    >
                      {{ isPro ? t.banner_desc_vip : t.banner_desc }}
                    </div>
                  </div>
                  <icon-base
                    name="ChevronRight"
                    class="w-5 h-5 transition-colors"
                    :class="
                      isPro
                        ? 'text-white'
                        : 'text-slate-500 group-hover:text-amber-400'
                    "
                  ></icon-base>
                </div>
              </button>

              <div class="space-y-3 mb-6">
                <div
                  class="text-xs font-bold uppercase tracking-wider mb-1 opacity-50 px-2"
                  :class="currentTheme.isDark ? 'text-white' : 'text-black'"
                >
                  {{ t.sec_personal }}
                </div>

                <div
                  class="rounded-xl overflow-hidden transition-all"
                  :class="currentTheme.isDark ? 'bg-white/5' : 'bg-black/5'"
                >
                  <button
                    @click="toggleProfileSection('theme')"
                    class="w-full flex items-center justify-between p-3"
                    :class="
                      currentTheme.isDark
                        ? 'hover:bg-white/5'
                        : 'hover:bg-black/5'
                    "
                  >
                    <div class="flex items-center gap-3">
                      <div
                        class="p-2 rounded-lg bg-purple-500/10 text-purple-500"
                      >
                        <icon-base name="Palette" class="w-4 h-4"></icon-base>
                      </div>
                      <span
                        class="text-sm font-medium"
                        :class="
                          currentTheme.isDark ? 'text-white' : 'text-slate-800'
                        "
                      >
                        {{ t.theme_title }}
                      </span>
                    </div>
                    <icon-base
                      :name="
                        profileSections.theme ? 'ChevronUp' : 'ChevronDown'
                      "
                      class="w-4 h-4 opacity-30"
                    ></icon-base>
                  </button>
                  <div
                    v-if="profileSections.theme"
                    class="p-3 pt-0 grid grid-cols-2 gap-2 animate-[fadeIn_0.2s]"
                  >
                    <button
                      v-for="(th, idx) in themes"
                      :key="th.id"
                      @click="handleThemeSelect(idx)"
                      class="flex flex-col p-2 rounded-lg border transition-all text-left gap-2 relative overflow-hidden"
                      :class="
                        themeIdx === idx
                          ? currentTheme.isDark
                            ? 'border-white/40 bg-white/10'
                            : 'border-black/20 bg-black/5'
                          : 'border-transparent hover:bg-black/5 dark:hover:bg-white/5'
                      "
                    >
                      <div
                        class="absolute inset-0 z-0 opacity-20"
                        :style="{ background: th.cardBg }"
                      ></div>
                      <div
                        class="relative z-10 flex items-center justify-between w-full"
                      >
                        <div
                          class="w-8 h-8 rounded-full shadow-sm border border-white/10 shrink-0"
                          :style="{ background: th.cardBg }"
                        ></div>
                        <icon-base
                          v-if="th.isPro && !isPro"
                          name="Lock"
                          class="w-3 h-3 opacity-50"
                        ></icon-base>
                      </div>
                      <div class="relative z-10">
                        <div
                          class="text-xs font-bold truncate"
                          :class="
                            currentTheme.isDark
                              ? 'text-white'
                              : 'text-neutral-900'
                          "
                        >
                          {{ th.name }}
                        </div>
                        <div class="text-[9px] opacity-50 truncate">
                          {{ th.meaning }}
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                <div
                  class="rounded-xl overflow-hidden transition-all"
                  :class="currentTheme.isDark ? 'bg-white/5' : 'bg-black/5'"
                >
                  <button
                    @click="toggleProfileSection('sound')"
                    class="w-full flex items-center justify-between p-3"
                    :class="
                      currentTheme.isDark
                        ? 'hover:bg-white/5'
                        : 'hover:bg-black/5'
                    "
                  >
                    <div class="flex items-center gap-3">
                      <div class="p-2 rounded-lg bg-pink-500/10 text-pink-500">
                        <icon-base name="Bell" class="w-4 h-4"></icon-base>
                      </div>
                      <span
                        class="text-sm font-medium"
                        :class="
                          currentTheme.isDark ? 'text-white' : 'text-slate-800'
                        "
                      >
                        {{ t.sound_title }}
                      </span>
                    </div>
                    <icon-base
                      :name="
                        profileSections.sound ? 'ChevronUp' : 'ChevronDown'
                      "
                      class="w-4 h-4 opacity-30"
                    ></icon-base>
                  </button>
                  <div
                    v-if="profileSections.sound"
                    class="p-3 pt-0 grid grid-cols-4 gap-2 animate-[fadeIn_0.2s]"
                  >
                    <button
                      v-for="(s, key) in visibleSounds"
                      :key="key"
                      @click="handleSoundSelect(key)"
                      class="flex flex-col items-center justify-center p-2 rounded-lg transition-all border active:scale-95"
                      :class="
                        sound === key
                          ? currentTheme.isDark
                            ? 'bg-white/10 border-white/50 text-white'
                            : 'bg-black/5 border-black/50 text-black'
                          : 'border-transparent hover:bg-black/5 dark:hover:bg-white/5 text-current opacity-60 hover:opacity-100'
                      "
                    >
                      <div class="relative">
                        <icon-base name="Bell" class="w-5 h-5 mb-1"></icon-base>
                        <div
                          v-if="s.isPro && !isPro"
                          class="absolute -top-1 -right-1 bg-amber-500 rounded-full w-2 h-2"
                        ></div>
                      </div>
                      <span class="text-[10px] font-bold">
                        {{ s.name[lang] || s.name }}
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              <div class="space-y-1">
                <div
                  class="text-xs font-bold uppercase tracking-wider mb-2 opacity-50 px-2"
                  :class="currentTheme.isDark ? 'text-white' : 'text-black'"
                >
                  {{ t.sec_general }}
                </div>
                <div
                  class="w-full flex items-center justify-between p-3 rounded-xl transition-colors"
                  :class="currentTheme.isDark ? 'bg-white/5' : 'bg-black/5'"
                >
                  <div class="flex items-center gap-3">
                    <div class="p-2 rounded-lg bg-blue-500/10 text-blue-500">
                      <icon-base name="Globe" class="w-4 h-4"></icon-base>
                    </div>
                    <span
                      class="text-sm font-medium"
                      :class="
                        currentTheme.isDark ? 'text-white' : 'text-slate-800'
                      "
                    >
                      {{ t.lang_title }}
                    </span>
                  </div>
                  <div class="flex gap-1">
                    <button
                      v-for="l in ['zh-CN', 'en', 'zh-TW']"
                      :key="l"
                      @click="lang = l"
                      class="text-[10px] px-2 py-1 rounded-md transition-colors"
                      :class="
                        lang === l
                          ? currentTheme.isDark
                            ? 'bg-white text-black'
                            : 'bg-black text-white'
                          : 'opacity-50 hover:opacity-100'
                      "
                    >
                      {{ l === "zh-CN" ? "简" : l === "en" ? "EN" : "繁" }}
                    </button>
                  </div>
                </div>
                <button
                  @click="vibrationOn = !vibrationOn"
                  class="w-full flex items-center justify-between p-3 rounded-xl transition-colors active:bg-black/5 dark:active:bg-white/10"
                  :class="
                    currentTheme.isDark
                      ? 'hover:bg-white/5'
                      : 'hover:bg-black/5'
                  "
                >
                  <div class="flex items-center gap-3">
                    <div
                      class="p-2 rounded-lg"
                      :class="
                        vibrationOn
                          ? 'bg-emerald-500/10 text-emerald-500'
                          : 'bg-gray-500/10 text-gray-500'
                      "
                    >
                      <icon-base name="Zap" class="w-4 h-4"></icon-base>
                    </div>
                    <span
                      class="text-sm font-medium"
                      :class="
                        currentTheme.isDark ? 'text-white' : 'text-slate-800'
                      "
                    >
                      {{ t.haptic_title }}
                    </span>
                  </div>
                  <div
                    class="w-10 h-6 rounded-full p-1 transition-colors"
                    :class="
                      vibrationOn
                        ? 'bg-emerald-500'
                        : 'bg-gray-300 dark:bg-gray-600'
                    "
                  >
                    <div
                      class="w-4 h-4 bg-white rounded-full shadow-sm transition-transform"
                      :class="vibrationOn ? 'translate-x-4' : 'translate-x-0'"
                    ></div>
                  </div>
                </button>
                <button
                  @click="handleSyncClick"
                  class="w-full flex items-center justify-between p-3 rounded-xl transition-colors active:bg-black/5 dark:active:bg-white/10"
                  :class="
                    currentTheme.isDark
                      ? 'hover:bg-white/5'
                      : 'hover:bg-black/5'
                  "
                >
                  <div class="flex items-center gap-3">
                    <div
                      class="p-2 rounded-lg bg-orange-500/10 text-orange-500"
                    >
                      <icon-base name="Refresh" class="w-4 h-4"></icon-base>
                    </div>
                    <span
                      class="text-sm font-medium"
                      :class="
                        currentTheme.isDark ? 'text-white' : 'text-slate-800'
                      "
                    >
                      {{ t.sync_title }}
                    </span>
                  </div>
                  <icon-base
                    v-if="!isPro"
                    name="Lock"
                    class="w-3 h-3 opacity-50"
                  ></icon-base>
                  <icon-base
                    v-else
                    name="ChevronRight"
                    class="w-4 h-4 opacity-30"
                  ></icon-base>
                </button>
              </div>
            </div>

            <div class="px-4 pb-4 mt-auto">
              <button
                v-if="isLoggedIn"
                @click="handleLogout"
                class="w-full py-3.5 rounded-xl font-bold mb-3 text-red-500 transition-colors"
                :class="
                  currentTheme.isDark
                    ? 'bg-red-500/10 hover:bg-red-500/20'
                    : 'bg-red-500/5 hover:bg-red-500/10'
                "
              >
                {{ lang === "en" ? "Log Out" : "退出登录" }}
              </button>

              <button
                @click="closeAllPanels"
                :class="pickerBtnClass"
                class="w-full py-3.5 rounded-xl font-bold"
              >
                {{ t.close_btn }}
              </button>
            </div>
          </div>

          <div
            v-if="panels.settings && !running"
            :class="pickerClass"
            class="animate-[slideUp_0.3s_ease-out]"
          >
            <div
              class="py-4 text-center font-bold text-lg tracking-tight shrink-0"
            >
              {{ t.settings_neuro }}
            </div>
            <div class="px-6 pb-6 overflow-y-auto">
              <div
                class="p-4 rounded-2xl border"
                :class="
                  currentTheme.isDark
                    ? 'bg-white/5 border-white/10'
                    : 'bg-black/5 border-black/5'
                "
              >
                <div class="flex items-center justify-between mb-4 relative">
                  <span class="font-bold flex items-center gap-2">{{
                    t.neuro_switch
                  }}</span>
                  <button
                    @click="toggleRandom"
                    class="w-12 h-7 rounded-full p-1 transition-colors duration-300"
                    :class="randomEnabled ? 'bg-emerald-500' : 'bg-gray-300'"
                  >
                    <div
                      class="w-5 h-5 bg-white rounded-full shadow-sm transform transition-transform duration-300"
                      :class="randomEnabled ? 'translate-x-5' : 'translate-x-0'"
                    ></div>
                  </button>
                </div>
                <div v-if="randomEnabled" class="animate-[fadeIn_0.2s]">
                  <input
                    type="number"
                    v-model.number="randomMin"
                    class="p-2 w-16 text-center text-black"
                  />
                  -
                  <input
                    type="number"
                    v-model.number="randomMax"
                    class="p-2 w-16 text-center text-black"
                  />
                </div>
              </div>
            </div>
            <div class="px-4 pb-4 mt-auto">
              <button
                @click="closeAllPanels"
                :class="pickerBtnClass"
                class="w-full py-3.5 rounded-xl font-bold"
              >
                {{ t.done_btn }}
              </button>
            </div>
          </div>

          <div
            v-if="panels.time && !running"
            :class="pickerClass"
            class="animate-[slideUp_0.3s_ease-out]"
          >
            <div class="grid grid-cols-3 gap-3 px-4 pb-6 pt-6 overflow-y-auto">
              <button
                v-for="c in timeChoices"
                :key="c.t"
                @click="applySeconds(c.v)"
                :class="pickerBtnClass"
                class="rounded-xl py-3 font-semibold shadow-sm transition active:scale-95"
              >
                {{ c.t }}
              </button>
            </div>
            <div class="px-4 pb-4 grid grid-cols-2 gap-3 mt-auto">
              <button
                @click="pickUnlimited"
                :class="pickerBtnClass"
                class="rounded-xl py-3 font-semibold"
              >
                ∞
              </button>
              <button
                @click="showCustomTime = true"
                :class="pickerBtnClass"
                class="rounded-xl py-3 font-semibold"
              >
                ...
              </button>
            </div>
            <div v-if="showCustomTime" class="px-4 pb-4 animate-[fadeIn_0.2s]">
              <div
                class="flex items-center gap-3 bg-black/5 dark:bg-white/5 p-2 rounded-xl"
              >
                <input
                  type="number"
                  v-model="customMin"
                  class="w-16 text-center text-black"
                />
                :
                <input
                  type="number"
                  v-model="customSec"
                  class="w-16 text-center text-black"
                />
                <button
                  @click="applyCustomTime"
                  class="ml-auto rounded-lg px-4 py-2 text-sm font-bold bg-black text-white"
                >
                  GO
                </button>
              </div>
            </div>
            <div class="px-4 pb-4">
              <button
                @click="closeAllPanels"
                :class="pickerBtnClass"
                class="w-full py-3.5 rounded-xl font-bold"
              >
                {{ t.close_btn }}
              </button>
            </div>
          </div>

          <div
            v-if="panels.tag && !running"
            :class="pickerClass"
            class="animate-[slideUp_0.3s_ease-out]"
          >
            <div class="grid grid-cols-3 gap-3 px-4 pb-4 pt-6 overflow-y-auto">
              <button
                v-for="tg in tags"
                :key="tg"
                @click="applyTagChoice(tg)"
                :class="pickerBtnClass"
                class="rounded-xl py-2.5 font-semibold text-sm"
              >
                {{ tg }}
              </button>
            </div>
            <div class="px-4 pb-4 mt-auto flex items-center gap-2">
              <input
                v-model="customTagInput"
                placeholder="+"
                class="flex-1 rounded-xl px-4 py-3 bg-black/5 text-black"
              />
              <button
                @click="addCustomTag"
                class="rounded-xl px-5 py-3 font-bold bg-black text-white"
              >
                ＋
              </button>
            </div>
            <div class="px-4 pb-4">
              <button
                @click="closeAllPanels"
                :class="pickerBtnClass"
                class="w-full py-3.5 rounded-xl font-bold"
              >
                {{ t.close_btn }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <transition name="fade">
        <div
          v-if="showRestToast"
          class="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 bg-black/80 text-white px-6 py-4 rounded-2xl backdrop-blur-md shadow-2xl flex flex-col items-center"
        >
          <icon-base
            name="Zap"
            class="w-8 h-8 text-amber-400 mb-2 animate-pulse"
          ></icon-base>
          <div class="text-lg font-bold">{{ t.neuro_toast_title }}</div>
          <div class="text-xs opacity-70">{{ t.neuro_toast_desc }}</div>
        </div>
      </transition>

      <transition name="fade">
        <div
          v-if="showCongrats"
          @click="showCongrats = false"
          class="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 bg-gradient-to-r from-amber-500 to-orange-600 text-white px-6 py-3 rounded-full shadow-xl flex items-center gap-3 cursor-pointer"
        >
          <!-- <icon-base name="Check" class="w-5 h-5"></icon-base> -->
          <span class="font-bold">{{ congratsText }}</span>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import {
  TRANSLATIONS,
  PRESETS,
  SOUNDS,
  THEMES,
  TAG_MAP,
} from "../constants/data";
import LoginView from "../components/LoginView.vue";
import PaymentView from "../components/PaymentView.vue";
import MembershipView from "../components/MembershipView.vue";
import VipSuccessView from "../components/VipSuccessView.vue";
import SyncView from "../components/SyncView.vue";
import StatsView from "../components/StatsView.vue";
import { startFocus, pingFocus, finishFocus, getUserInfo } from "@/api";

export default {
  name: "ZenoApp",
  components: {
    LoginView,
    PaymentView,
    MembershipView,
    VipSuccessView,
    SyncView,
    StatsView,
  },
  data() {
    return {
      // 持久化状态
      isLoggedIn: JSON.parse(localStorage.getItem("zeno_isLoggedIn")) || false,
      isPro: JSON.parse(localStorage.getItem("zeno_isPro")) || false,
      themeIdx: JSON.parse(localStorage.getItem("zeno_themeIdx")) || 0,
      sound: JSON.parse(localStorage.getItem("zeno_sound")) || "bell",
      history: JSON.parse(localStorage.getItem("zeno_history")) || [],
      randomEnabled:
        JSON.parse(localStorage.getItem("zeno_randomEnabled")) || false,
      lang: JSON.parse(localStorage.getItem("zeno_lang")) || "zh-CN",
      randomMin: JSON.parse(localStorage.getItem("zeno_randomMin")) || 3,
      randomMax: JSON.parse(localStorage.getItem("zeno_randomMax")) || 5,

      // 临时状态
      device: "IPHONE",
      seconds: 25 * 60,
      timerState: "idle",
      currentView: "timer",
      panels: {
        time: false,
        tag: false,
        theme: false,
        sound: false,
        settings: false,
        profile: false,
      },

      tag: "",
      tags: [],
      customTags: [],
      mode: "down",
      bgmOn: false,
      nextRandomTrigger: null,
      showRestToast: false,
      vibrationOn: true,
      showCongrats: false,
      containerDim: { w: 300, h: 600 },
      profileSections: { theme: false, sound: false },

      initialTime: 25 * 60,
      pausedTime: null,
      audioRef: null,
      sfxRef: null,
      intervalId: null,

      customMin: 25,
      customSec: 0,
      showCustomTime: false,
      customTagInput: "",

      presets: PRESETS,
      sounds: SOUNDS,
      themes: THEMES,
      deviceTabs: ["IPHONE", "IPAD", "WATCH", "WEB"],
      timeChoices: [
        { t: "15:00", v: 900 },
        { t: "25:00", v: 1500 },
        { t: "30:00", v: 1800 },
        { t: "45:00", v: 2700 },
        { t: "60:00", v: 3600 },
        { t: "90:00", v: 5400 },
      ],
      currentSessionId: null, // 新增：用于存储当前专注的 session_id
      pingInterval: null, // 新增：用于心跳上报的定时器
    };
  },
  computed: {
    t() {
      return TRANSLATIONS[this.lang];
    },
    currentTheme() {
      return THEMES[this.themeIdx];
    },
    currentPreset() {
      return PRESETS[this.device];
    },
    running() {
      return this.timerState === "running";
    },
    timerStyle() {
      return {
        fontFeatureSettings: '"tnum" on',
        fontSize: this.timeSize,
        opacity: this.running ? 0.9 : 1,
      };
    },
    pickerClass() {
      return `${
        this.currentTheme.isDark
          ? "bg-neutral-900 text-white border-neutral-700"
          : "bg-white text-neutral-900 border-neutral-200"
      } mx-auto w-full rounded-t-3xl border-t shadow-2xl safe-bottom absolute bottom-0 inset-x-0 z-20 flex flex-col max-h-[85%]`;
    },
    pickerBtnClass() {
      return `${
        this.currentTheme.isDark
          ? "bg-neutral-800 hover:bg-neutral-700"
          : "bg-neutral-100 hover:bg-neutral-200"
      }`;
    },
    btnPad() {
      return this.device === "WATCH"
        ? "px-4 py-2 text-sm"
        : "px-6 py-3 text-base";
    },
    timeSize() {
      if (this.device === "IPHONE" || this.device === "WATCH") {
        const baseW = PRESETS[this.device].maxW;
        return `${Math.round(baseW * 0.28)}px`;
      }
      const { w, h } = this.containerDim;
      if (!w || !h) return "120px";
      const availableH = Math.max(100, h - 200);
      const availableW = w - 40;
      let fontSize = Math.min(availableW * 0.25, availableH * 0.5);
      fontSize = Math.max(48, Math.min(fontSize, 200));
      if (this.running) fontSize *= 0.95;
      return `${Math.round(fontSize)}px`;
    },
    parsedHistory() {
      return this.history
        .map((h) => ({ ...h, date: new Date(h.date) }))
        .sort((a, b) => b.date - a.date);
    },
    statsSummary() {
      const totalMins = this.parsedHistory.reduce(
        (acc, cur) => acc + cur.duration,
        0
      );
      const totalHours = Math.floor(totalMins / 60);
      let streak = 0;
      const today = new Date();
      const uniqueDates = [
        ...new Set(this.parsedHistory.map((h) => h.date.toDateString())),
      ]
        .map((d) => new Date(d))
        .sort((a, b) => b - a);
      if (
        uniqueDates.length > 0 &&
        (this.isSameDay(uniqueDates[0], today) ||
          this.isSameDay(uniqueDates[0], new Date(today.getTime() - 86400000)))
      ) {
        streak = 1;
        for (let i = 0; i < uniqueDates.length - 1; i++) {
          if (
            Math.round((uniqueDates[i] - uniqueDates[i + 1]) / 86400000) === 1
          )
            streak++;
          else break;
        }
      }
      return { totalHours, totalTasks: this.parsedHistory.length, streak };
    },
    monthHeatmap() {
      const today = new Date();
      const year = today.getFullYear();
      const month = today.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const days = [];
      for (let i = 1; i <= daysInMonth; i++) {
        const currentDate = new Date(year, month, i);
        const tasksCount = this.parsedHistory.filter((h) =>
          this.isSameDay(h.date, currentDate)
        ).length;
        days.push({
          date: currentDate,
          tasksCount,
          dayNum: i,
          isToday: this.isSameDay(currentDate, today),
        });
      }
      return days;
    },
    visibleSounds() {
      const filtered = {};
      Object.keys(this.sounds).forEach((key) => {
        if (key !== "ping") {
          filtered[key] = this.sounds[key];
        }
      });
      return filtered;
    },
  },
  watch: {
    isLoggedIn(v) {
      localStorage.setItem("zeno_isLoggedIn", JSON.stringify(v));
    },
    isPro(v) {
      localStorage.setItem("zeno_isPro", JSON.stringify(v));
    },
    themeIdx(v) {
      localStorage.setItem("zeno_themeIdx", JSON.stringify(v));
    },
    sound(v) {
      localStorage.setItem("zeno_sound", JSON.stringify(v));
    },
    history: {
      handler(v) {
        localStorage.setItem("zeno_history", JSON.stringify(v));
      },
      deep: true,
    },
    randomEnabled(v) {
      localStorage.setItem("zeno_randomEnabled", JSON.stringify(v));
    },
    lang(v) {
      localStorage.setItem("zeno_lang", JSON.stringify(v));
      this.initTags();
    },
    randomMin(v) {
      localStorage.setItem("zeno_randomMin", JSON.stringify(v));
    },
    randomMax(v) {
      localStorage.setItem("zeno_randomMax", JSON.stringify(v));
    },
    bgmOn(v) {
      if (this.audioRef) {
        if (v) this.audioRef.play().catch(() => {});
        else this.audioRef.pause();
      }
    },
    timerState(newState) {
      if (newState === "running") {
        const remaining = this.pausedTime || this.seconds;
        this._endTime = Date.now() + remaining * 1000;
        if (this.randomEnabled && this.isPro && !this.nextRandomTrigger) {
          const minSec = this.randomMin * 60;
          const maxSec = this.randomMax * 60;
          const nextDelay =
            Math.floor(Math.random() * (maxSec - minSec + 1)) + minSec;
          const safeDelay = Math.min(nextDelay, remaining - 10);
          if (safeDelay > 0)
            this.nextRandomTrigger = Date.now() + safeDelay * 1000;
        }
        this.intervalId = setInterval(() => {
          const now = Date.now();
          const left = Math.ceil((this._endTime - now) / 1000);
          if (left <= 0) {
            this.seconds = 0;
            this.finish();
          } else {
            this.seconds = left;
            if (
              this.randomEnabled &&
              this.nextRandomTrigger &&
              now >= this.nextRandomTrigger
            ) {
              this.triggerRandomAlert();
              const minSec = this.randomMin * 60;
              const maxSec = this.randomMax * 60;
              const nextDelay =
                Math.floor(Math.random() * (maxSec - minSec + 1)) + minSec;
              const safeDelay = Math.min(nextDelay, left - 10);
              if (safeDelay > 0) {
                this.nextRandomTrigger = Date.now() + safeDelay * 1000;
              } else {
                this.nextRandomTrigger = null;
              }
            }
          }
        }, 1000);

        // 如果是新开始的任务（非暂停恢复），调用 API 开始专注
        if (!this.pausedTime && this.isLoggedIn) {
          this.apiStartFocus();
        } else if (this.isLoggedIn && this.currentSessionId) {
          this.startPing();
        }
      } else {
        // 暂停或停止时，清除心跳
        this.stopPing();
        clearInterval(this.intervalId); // 清除本地计时器
      }
    },
  },
  created() {
    this.initTags();
    if (this.history.length === 0) this.generateMockHistory();

    // 如果已登录，获取用户信息
    if (this.isLoggedIn) {
      this.fetchUserInfo();
    }
  },
  mounted() {
    this.updateContainerDim();
    window.addEventListener("resize", this.updateContainerDim);
    this.checkPayCallback();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateContainerDim);
    clearInterval(this.intervalId);
  },
  methods: {
    initTags() {
      this.tags = [...this.t.tags];
      if (
        !this.customTags.includes(this.tag) &&
        !this.t.tags.includes(this.tag)
      ) {
        this.tag = this.t.tags[0];
      }
    },
    generateMockHistory() {
      const mock = [];
      const today = new Date();
      for (let i = 1; i <= 28; i++) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        if (Math.random() > 0.4) {
          const count = Math.floor(Math.random() * 5) + 1;
          for (let k = 0; k < count; k++) {
            mock.push({
              id: Date.now() - i * 10000 - k * 1000,
              date: d.toISOString(),
              duration: 25,
              tag: this.t.tags[0],
            });
          }
        }
      }
      this.history = mock;
    },
    updateContainerDim() {
      if (this.$refs.cardRef) {
        this.containerDim = {
          w: this.$refs.cardRef.clientWidth,
          h: this.$refs.cardRef.clientHeight,
        };
      }
    },
    fmt(s) {
      return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
    },
    isSameDay(d1, d2) {
      return (
        d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getDate() === d2.getDate()
      );
    },
    warmUpAudio() {
      if (!this.audioRef) {
        const el = new Audio(
          "https://cdn.pixabay.com/download/audio/2022/03/15/audio_9a33a.mp3?filename=soft-piano-ambient-10911.mp3"
        );
        el.loop = true;
        this.audioRef = el;
      }
      if (this.sfxRef)
        this.sfxRef
          .play()
          .then(() => this.sfxRef.pause())
          .catch(() => {});
    },
    playSound(soundKey) {
      if (soundKey === "mute") return;
      const s = SOUNDS[soundKey] || SOUNDS["bell"];
      if (s && s.url) {
        if (this.bgmOn && this.audioRef) this.audioRef.volume = 0.2;
        if (this.sfxRef) {
          this.sfxRef.pause();
          this.sfxRef.currentTime = 0;
        }
        this.sfxRef = new Audio(s.url);
        this.sfxRef.volume = 0.8;
        this.sfxRef
          .play()
          .then(() => {
            setTimeout(() => {
              if (this.bgmOn && this.audioRef) this.audioRef.volume = 1;
            }, 2000);
          })
          .catch((e) => console.log("SFX block", e));
      }
    },
    triggerRandomAlert() {
      this.playSound("ping");
      this.showRestToast = true;
      setTimeout(() => (this.showRestToast = false), 10000);
      if (this.vibrationOn && "vibrate" in navigator)
        navigator.vibrate([100, 50, 100]);
    },
    start(customDuration = null) {
      this.warmUpAudio();
      let startSecs = this.seconds;
      if (customDuration !== null) {
        startSecs = customDuration;
        this.seconds = customDuration;
      }
      if (startSecs === 0) return;
      if (this.timerState === "idle" || customDuration !== null) {
        this.initialTime = startSecs;
        this.pausedTime = startSecs;
      }
      this.timerState = "running";
      this.closeAllPanels();
    },
    pause() {
      this.timerState = "paused";
      this.pausedTime = this.seconds;
    },
    async finish() {
      this.timerState = "idle";
      this.pausedTime = null;
      this.nextRandomTrigger = null;
      this.playSound(this.sound);
      if (this.tag !== this.t.tags[1] && this.tag !== this.t.tags[8]) {
        const recordDuration =
          this.seconds === 0
            ? Math.ceil(this.initialTime / 60)
            : Math.ceil((this.initialTime - this.seconds) / 60);
        if (recordDuration > 0) {
          const newItem = {
            id: Date.now(),
            date: new Date().toISOString(),
            duration: recordDuration,
            tag: this.tag,
          };
          this.history.unshift(newItem);
        }
      }

      // 调用 API 结束专注 [cite: 129]
      if (this.isLoggedIn && this.currentSessionId) {
        try {
          await finishFocus(this.currentSessionId);
        } catch (e) {
          console.error("Failed to finish focus session", e);
        }
        this.currentSessionId = null;
      }
      this.stopPing(); // 停止心跳

      if (this.tag === this.t.tags[1] || this.tag === this.t.tags[8]) {
        this.resetToHome();
        return;
      }

      this.showCongrats = true;
      this.congratsText = this.t.congrats_title;
      // 新增：3秒后自动隐藏
      setTimeout(() => {
        this.showCongrats = false;
      }, 1500);

      if (this.vibrationOn && "vibrate" in navigator)
        navigator.vibrate([60, 40, 60]);
    },
    endNow() {
      this.finish();
    },
    resetToHome() {
      this.seconds = 25 * 60;
      this.tag = this.t.tags[0];
      this.mode = "down";
      this.timerState = "idle";
      this.pausedTime = null;
      this.showCongrats = false;
    },
    togglePanel(n) {
      if (this.panels[n]) {
        this.panels[n] = false;
      } else {
        this.closeAllPanels();
        this.panels[n] = true;
      }
      if (n !== "time") this.showCustomTime = false;
    },
    closeAllPanels() {
      Object.keys(this.panels).forEach((k) => (this.panels[k] = false));
      this.showCustomTime = false;
    },
    toggleProfileSection(s) {
      this.profileSections[s] = !this.profileSections[s];
    },
    handleProfileClick() {
      if (!this.isLoggedIn) {
        this.currentView = "login";
      } else {
        this.togglePanel("profile");
      }
    },
    handleThemeSelect(idx) {
      if (THEMES[idx].isPro && !this.isPro) {
        this.currentView = "payment";
        return;
      }
      this.themeIdx = idx;
    },
    handleSoundSelect(key) {
      if (SOUNDS[key].isPro && !this.isPro) {
        this.currentView = "payment";
        return;
      }
      this.sound = key;
      this.playSound(key);
    },
    toggleRandom() {
      if (!this.isPro) {
        this.currentView = "payment";
        return;
      }
      this.randomEnabled = !this.randomEnabled;
    },
    handleSyncClick() {
      if (!this.isPro) {
        this.currentView = "payment";
        return;
      }
      this.currentView = "sync";
    },
    handleLoginSuccess() {
      this.isLoggedIn = true;
      this.currentView = "timer";
      // 登录成功后刷新用户信息
      this.fetchUserInfo();
      setTimeout(() => this.togglePanel("profile"), 300);
    },

    // 支付成功后处理
    handlePaymentSuccess() {
      this.fetchUserInfo();
      this.currentView = "vip_success";
    },
    applySeconds(sec) {
      this.mode = "down";
      this.seconds = sec;
      this.closeAllPanels();
    },
    pickUnlimited() {
      this.mode = "up";
      this.seconds = 0;
      this.closeAllPanels();
      this.start();
    },
    applyCustomTime() {
      let mm = parseInt(this.customMin) || 0;
      let ss = parseInt(this.customSec) || 0;
      mm = Math.max(0, Math.min(999, mm));
      ss = Math.max(0, Math.min(59, ss));
      this.applySeconds(mm * 60 + ss);
    },
    applyTagChoice(t) {
      this.tag = t;
      if (!this.running && TAG_MAP[t]) {
        this.mode = "down";
        this.seconds = TAG_MAP[t];
      }
      this.closeAllPanels();
    },
    addCustomTag() {
      const t = (this.customTagInput || "").trim();
      if (t) {
        if (!this.tags.includes(t)) this.tags.push(t);
        if (!this.customTags.includes(t)) this.customTags.push(t);
        this.tag = t;
        this.closeAllPanels();
        this.customTagInput = "";
      }
    },
    removeCustomTag(t) {
      this.tags = this.tags.filter((x) => x !== t);
      this.customTags = this.customTags.filter((x) => x !== t);
      if (this.tag === t) this.tag = this.t.tags[0];
    },
    async fetchUserInfo() {
      try {
        const data = await getUserInfo();
        // 根据返回更新状态 [cite: 77]
        // data.vip_type: 0非会员 1普通会员 2终身会员
        this.isPro = data.vip_type > 0;
        // 可以根据需要保存更多用户信息
      } catch (e) {
        if (e.message.includes("401")) {
          this.isLoggedIn = false; // Token 过期处理
        }
      }
    },

    // 封装 API 调用
    async apiStartFocus() {
      try {
        const sessionId = await startFocus(); // [cite: 114]
        this.currentSessionId = sessionId; // [cite: 124] 返回的是 string
        this.startPing();
      } catch (e) {
        console.error("Failed to start focus session", e);
      }
    },

    startPing() {
      this.stopPing(); // 防止重复
      // 接口备注：计时器启动后需要每30s或1min上报一次 [cite: 150]
      this.pingInterval = setInterval(() => {
        if (this.currentSessionId) {
          pingFocus(this.currentSessionId).catch((e) =>
            console.error("Ping failed", e)
          );
        }
      }, 30000); // 30秒
    },

    stopPing() {
      if (this.pingInterval) {
        clearInterval(this.pingInterval);
        this.pingInterval = null;
      }
    },
    handleLogout() {
      // 1. 重置界面状态
      this.isLoggedIn = false;
      this.isPro = false; // 如果非会员可以保留，根据业务需求调整

      // 2. 清理本地存储 (localStorage)
      // 注意：data() 中的 isLoggedIn 会通过 watch 自动更新 localStorage['zeno_isLoggedIn']
      // 但我们需要手动清理 Token (上一轮接入 API 时约定的 Key)
      localStorage.removeItem("zeno_token");

      // 3. 关闭所有面板
      this.closeAllPanels();

      // 4. (可选) 如果有专注心跳，停止心跳上报
      if (this.stopPing) {
        this.stopPing();
      }
    },
    checkPayCallback() {
      const { payStatus } = this.$route.query;
      if (payStatus) {
        // 清除 URL 参数，避免刷新再次触发
        this.$router.replace({ query: {} });
        if (payStatus === "success") {
          this.handlePaymentSuccess();
        } else if (payStatus === "failed") {
          this.showCongrats = true;
          this.congratsText = "支付失败";
          setTimeout(() => {
            this.showCongrats = false;
          }, 1500);
        }
      }
    },
  },
};
</script>
